<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Cupons de Desconto</h1>
      <v-btn color="primary" @click="handleCreateCoupon">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Cupom
      </v-btn>
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <v-row class="mb-6" dense>
          <v-col cols="12" md="4">
            <v-text-field v-model="searchTerm" placeholder="Pesquisar por Código" clearable hide-details
              prepend-inner-icon="mdi-magnify" @input="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="selectedType" :items="typeOptions" placeholder="Filtrar por Tipo" clearable hide-details
              @update:model-value="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="selectedIsActive" :items="activeOptions" placeholder="Filtrar por Status" clearable
              hide-details @update:model-value="onFiltersChange" />
          </v-col>
        </v-row>

        <v-data-table-server :headers="columns" :items="coupons" :items-length="totalItems" :loading="isLoading"
          :items-per-page="itemsPerPage" :page="page" :no-data-text="noDataMessage" class="elevation-0"
          @update:page="page = $event" @update:items-per-page="itemsPerPage = $event">

          <template v-slot:item.type="{ item }">
            <span>{{ formatType(item.type) }}</span>
          </template>

          <template v-slot:item.value="{ item }">
            <span>{{ formatValue(item.value, item.type) }}</span>
          </template>

          <template v-slot:item.expires_at="{ item }">
            <span>{{ formatExpiresAt(item.expires_at) }}</span>
          </template>

          <template v-slot:item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon icon="mdi-pencil" color="warning" size="large" class="mr-2" @click="handleEdit(item)"
              title="Editar" />
            <v-icon icon="mdi-delete" color="error" size="large" @click="handleDelete(item)" title="Excluir" />
          </template>
        </v-data-table-server>

        <v-alert v-if="hasError && !isLoading" type="error" variant="tonal" class="mt-4 text-center">
          Ocorreu um erro ao carregar os dados.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Tem certeza de que deseja excluir o cupom
          <strong>{{ itemToDelete.code }}</strong> (Valor: <strong>{{ formatValue(itemToDelete.value, itemToDelete.type)
          }}</strong>)?
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn color="secondary" variant="flat" @click="deleteDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useToastStore } from '@/store/toast'
import { useLoadingStore } from '@/store/loadingStore' 

const router = useRouter()
const toastStore = useToastStore()
const loadingStore = useLoadingStore()

// Estado
const coupons = ref([])
const isLoading = ref(true)
const hasError = ref(false)
const searchTerm = ref('')
const selectedType = ref(null)
const selectedIsActive = ref(null)

// Paginação
const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)

// Exclusão
const deleteDialog = ref(false)
const itemToDelete = ref({})

const typeOptions = [
  { title: 'Porcentagem (%)', value: 'percent' },
  { title: 'Valor Fixo (R$)', value: 'fixed' }
]
const activeOptions = [
  { title: 'Ativo', value: '1' },
  { title: 'Inativo', value: '0' }
]

const isActiveTagMap = {
  0: 'error',
  1: 'success'
}

const noDataMessage = computed(() => {
  if (isLoading.value) return 'Carregando...'
  return 'Nenhum cupom encontrado.'
})

const columns = [
  { title: 'Código', key: 'code', sortable: true },
  { title: 'Tipo', key: 'type', sortable: false },
  { title: 'Valor', key: 'value', sortable: false },
  { title: 'Uso (Atual)', key: 'usage_count', sortable: false },
  { title: 'Limite', key: 'usage_limit', sortable: false },
  { title: 'Expiração', key: 'expires_at', sortable: true },
  { title: 'Ativo', key: 'is_active', sortable: true },
  { title: 'Ações', key: 'actions', sortable: false }
]

const loadCoupons = async () => {
  isLoading.value = true
  hasError.value = false
  loadingStore.show()
  try {
    const params = {
      page: page.value,
      per_page: itemsPerPage.value,
      code: searchTerm.value,
      type: selectedType.value,
      is_active: selectedIsActive.value
    }

    const response = await api.get('/coupons', { params })

    coupons.value = response.data.data
    totalItems.value = response.data.total
    itemsPerPage.value = response.data.per_page
    page.value = response.data.current_page
    
  } catch (error) {
    console.error('Erro ao carregar cupons:', error)
    hasError.value = true
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar a lista de cupons.',
      color: 'error'
    })
  } finally {
    isLoading.value = false
    loadingStore.hide()
  }
}

watch([page, itemsPerPage], () => {
  loadCoupons()
})

const onFiltersChange = () => {
  page.value = 1
  loadCoupons()
}

const handleCreateCoupon = () => {
  router.push('/app/cupons/novo')
}

const handleEdit = async (row) => {
  router.push({ name: 'coupon-edit', params: { id: row.id } })
}

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = async () => {
  deleteDialog.value = false
  try {
    await api.delete(`/coupons/${itemToDelete.value.id}`)
    toastStore.showToast({
      message: 'Cupom excluído com sucesso!',
      color: 'success'
    })
    
    await loadCoupons()
  } catch (error) {
    console.error('Erro ao excluir cupom:', error)
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao excluir cupom.',
      color: 'error'
    })
  }
}

function formatValue(value, type) {
  const numValue = parseFloat(value);
  if (type === 'percent') {
    return `${numValue.toFixed(2)}%`;
  }
  return numValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatType(type) {
  if (type === 'percent') {
    return `Porcentagem`;
  }else if(type === 'fixed'){
    return `Fixo`;
  }else{
    return '';
  }
}

function formatExpiresAt(dateString) {
  if (!dateString) return 'Sem expiração';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
}

function formatIsActive(is_active) {
  return is_active === 1 ? 'Ativo' : 'Desativado'
}

onMounted(() => {
  loadCoupons()
})
</script>

<style scoped>
/* Estilos específicos se necessário */
</style>